import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Check, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useEffect } from "react";

const FEEDBACK_TYPE_LABELS: Record<string, string> = {
  incorrect_answer: "Incorrect Answer",
  unclear_question: "Unclear Question",
  typo: "Typo",
  outdated_info: "Outdated Info",
  missing_explanation: "Missing Explanation",
  other: "Other",
};

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  accepted: { color: "bg-green-500", label: "Accepted" },
  rejected: { color: "bg-red-500", label: "Rejected" },
  resolved: { color: "bg-primary", label: "Resolved" },
};

export function FeedbackNotifications() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const { data: unreadFeedback, isLoading } = useQuery({
    queryKey: ["unread-feedback-responses", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return [];

      const { data, error } = await supabase
        .from("mcq_user_feedback")
        .select(`
          *,
          mcqs(stem)
        `)
        .eq("user_id", session.user.id)
        .not("admin_response", "is", null)
        .is("user_viewed_response_at", null)
        .order("reviewed_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!session?.user?.id,
  });

  const markAsViewed = useMutation({
    mutationFn: async (feedbackId: string) => {
      const { error } = await supabase
        .from("mcq_user_feedback")
        .update({ user_viewed_response_at: new Date().toISOString() })
        .eq("id", feedbackId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unread-feedback-responses"] });
      toast.success("Marked as read");
    },
  });

  const markAllAsViewed = useMutation({
    mutationFn: async () => {
      if (!session?.user?.id) return;

      const { error } = await supabase
        .from("mcq_user_feedback")
        .update({ user_viewed_response_at: new Date().toISOString() })
        .eq("user_id", session.user.id)
        .not("admin_response", "is", null)
        .is("user_viewed_response_at", null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unread-feedback-responses"] });
      toast.success("All notifications marked as read");
    },
  });

  // Show toast on new responses
  useEffect(() => {
    if (unreadFeedback && unreadFeedback.length > 0) {
      const latestFeedback = unreadFeedback[0];
      const isNew = latestFeedback.reviewed_at && 
        new Date(latestFeedback.reviewed_at).getTime() > Date.now() - 5000; // Last 5 seconds

      if (isNew) {
        toast.info("You have new feedback responses!", {
          description: "Check your notifications to see admin replies",
        });
      }
    }
  }, [unreadFeedback]);

  if (isLoading || !unreadFeedback || unreadFeedback.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Feedback Responses
          </CardTitle>
          <CardDescription>You'll see admin responses to your feedback here</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No new responses
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Feedback Responses
              <Badge variant="destructive" className="ml-2">{unreadFeedback.length}</Badge>
            </CardTitle>
            <CardDescription>Admin responses to your feedback submissions</CardDescription>
          </div>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => markAllAsViewed.mutate()}
            disabled={markAllAsViewed.isPending}
          >
            <Check className="h-4 w-4 mr-2" />
            Mark all as read
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {unreadFeedback.map((feedback) => (
            <div key={feedback.id} className="border rounded-lg p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {FEEDBACK_TYPE_LABELS[feedback.feedback_type]}
                    </Badge>
                    <Badge className={STATUS_CONFIG[feedback.status]?.color}>
                      {STATUS_CONFIG[feedback.status]?.label}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                    {(feedback.mcqs as any)?.stem || "Question not found"}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => markAsViewed.mutate(feedback.id)}
                  disabled={markAsViewed.isPending}
                >
                  <Check className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="bg-muted rounded-md p-3">
                <div className="flex items-start gap-2">
                  <MessageSquare className="h-4 w-4 text-primary mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Admin Response:</p>
                    <p className="text-sm text-muted-foreground mt-1">{feedback.admin_response}</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Responded {format(new Date(feedback.reviewed_at!), "PPp")}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function FeedbackNotificationBadge() {
  const { session } = useAuth();

  const { data: unreadCount } = useQuery({
    queryKey: ["unread-feedback-count", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return 0;

      const { count } = await supabase
        .from("mcq_user_feedback")
        .select("*", { count: "exact", head: true })
        .eq("user_id", session.user.id)
        .not("admin_response", "is", null)
        .is("user_viewed_response_at", null);

      return count || 0;
    },
    enabled: !!session?.user?.id,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  if (!unreadCount || unreadCount === 0) return null;

  return (
    <Badge variant="destructive" className="ml-2">
      {unreadCount}
    </Badge>
  );
}
